/**
 * Message classification logic for adventure play messages.
 * Separate classification paths for Player vs Narrator messages.
 */

// ── Player intent → category mapping ──
const PLAYER_INTENT_MAP: Record<string, string> = {
  ACTION: "action", COMBAT: "action", COMPOUND: "compound",
  SOCIAL: "social", DIALOGUE: "social",
  MOVEMENT: "exploration", EXPLORATION: "exploration",
  ITEM_USE: "item_use", TRADE: "item_use",
  QUESTION: "question", PLANNING: "question",
  META: "ooc", WAIT: "wait",
  SETUP: "system", CHARACTER_SELECT: "system",
  INTROSPECTION: "introspection",
  GM_QUERY: "gm_query",
};

// ── Narrator content → category classification ──
function classifyNarrator(meta: any): string {
  if (meta?.is_inner_voice) return "introspection";
  if (meta?.is_gm_query) return "gm_query";
  if (meta?.is_ooc) return "ooc";
  if (meta?.dice_rolls?.length > 0 || meta?.state_changes?.combatants) return "action";
  if (meta?.state_changes?.current_location) return "exploration";
  if (meta?.state_changes?.inventory_changes?.length > 0) return "item_use";
  if (meta?.state_changes?.transaction) return "trade";
  // mechanic_results are supplementary — don't classify as system
  // Check for dialogue-heavy responses
  const intent = meta?.intent || meta?.intent_classification?.primary_intent;
  if (intent === "DIALOGUE" || intent === "SOCIAL") return "dialogue";
  if (intent === "COMBAT" || intent === "ACTION") return "action";
  if (intent === "EXPLORATION" || intent === "MOVEMENT") return "exploration";
  return "story";
}

// ── Player content → category classification ──
function classifyPlayer(meta: any): string {
  const intent = meta?.intent || meta?.intent_classification?.primary_intent;
  if (meta?.is_ooc) return "ooc";
  if (meta?.is_gm_query || intent === "GM_QUERY") return "gm_query";
  if (intent) return PLAYER_INTENT_MAP[intent] || "action";
  return "action"; // default player messages to action
}

export function classifyMessage(msg: any): string {
  const meta = msg.metadata as any;

  // User override takes priority
  if (meta?.override_category) return meta.override_category;

  if (msg.role === "user") return classifyPlayer(meta);
  return classifyNarrator(meta);
}

// ── Segment-level classification ──
import type { NarratorSegment } from "./parse-narrator-segments";

const SEGMENT_CATEGORY_MAP: Record<NarratorSegment["type"], string> = {
  narration: "story",
  dialogue: "dialogue",
  action: "action",
  inner_voice: "introspection",
  note: "system",
};

/** Classify a single parsed narrator segment. */
export function classifySegment(seg: NarratorSegment): string {
  return SEGMENT_CATEGORY_MAP[seg.type] || "story";
}

/** Classify all segments in a narrator message, returning unique categories in order. */
export function classifySegments(segments: NarratorSegment[]): string[] {
  const seen = new Set<string>();
  const cats: string[] = [];
  for (const seg of segments) {
    const cat = classifySegment(seg);
    if (!seen.has(cat)) { seen.add(cat); cats.push(cat); }
  }
  return cats;
}

// ── Category badge config ──

export const CATEGORY_BADGES: Record<string, { label: string; className: string }> = {
  action: { label: "⚔️ Action", className: "border-orange-500/30 text-orange-500" },
  social: { label: "💬 Social", className: "border-blue-500/30 text-blue-500" },
  dialogue: { label: "💬 Dialogue", className: "border-blue-500/30 text-blue-500" },
  exploration: { label: "🗺️ Explore", className: "border-green-500/30 text-green-500" },
  item_use: { label: "🎒 Item", className: "border-amber-500/30 text-amber-500" },
  trade: { label: "💰 Trade", className: "border-yellow-500/30 text-yellow-500" },
  question: { label: "❓ Question", className: "border-sky-500/30 text-sky-500" },
  wait: { label: "⏳ Wait", className: "border-muted-foreground/30 text-muted-foreground" },
  system: { label: "⚙️ System", className: "border-purple-500/30 text-purple-500" },
  ooc: { label: "ℹ️ OOC", className: "border-muted-foreground/30 text-muted-foreground" },
  story: { label: "✨ Story", className: "border-primary/30 text-primary" },
  compound: { label: "🔀 Multi-Action", className: "border-teal-500/30 text-teal-500" },
  introspection: { label: "🧠 Inner Voice", className: "border-violet-500/30 text-violet-500" },
  gm_query: { label: "🎲 Game Master", className: "border-amber-500/30 text-amber-500" },
};

/** Derive categories from sub_actions metadata (for compound messages). */
export function categoriesFromSubActions(subActions: Array<{ intent?: string; type?: string }>): string[] {
  const seen = new Set<string>();
  const cats: string[] = [];
  for (const sa of subActions) {
    const intent = sa.intent || sa.type || "ACTION";
    const cat = PLAYER_INTENT_MAP[intent] || "action";
    if (!seen.has(cat)) { seen.add(cat); cats.push(cat); }
  }
  return cats;
}

/**
 * Derive ordered, de-duplicated categories from a primary intent plus any
 * secondary intents. Used to render multiple badges when the director tags a
 * single message with several overlapping intents (multi-intent support).
 */
export function categoriesFromIntents(
  primaryIntent?: string | null,
  secondaryIntents?: string[] | null,
): string[] {
  const seen = new Set<string>();
  const cats: string[] = [];
  const push = (intent?: string | null) => {
    if (!intent) return;
    const cat = PLAYER_INTENT_MAP[intent] || "action";
    if (!seen.has(cat)) { seen.add(cat); cats.push(cat); }
  };
  push(primaryIntent);
  for (const s of secondaryIntents || []) push(s);
  return cats;
}
